import { AddModalPresenter } from "./AddModalPresenter";
import {
  ICON_TYPE,
  NOTICE_COLOR,
  Notice,
} from "api/interfaces/noticeInterface.interface";
import { deleteNotice, postNotice, putNotice } from "api/settingAPI";
import { useCallback, useEffect, useState } from "react";
import { useQueryClient } from "react-query";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { useAppSelector } from "redux/hooks";
import { ValidateMsg } from "api/interfaces/commonInterface.interface";
import { isEmpty, isNumber } from "lodash";
import { checkEmpty, extractTextFromHTML, toTrim } from "utils/functions";
import useStatusMutation from "hook/useStatusMutation";
import useApiError from "hook/useApiError";
interface Props {
  isEdit?: boolean;
  selectedNotice?: Notice | undefined;
  onModal: () => void;
  validateMsg?: ValidateMsg;
}

export function AddModalContainer(props: Props): JSX.Element {
  const queryClient = useQueryClient();
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const { lang, notifyError, notifySuccess, intlString } = useApiError();

  const [isPermanently, setIsPermanently] = useState<boolean>(true);
  const [validateMsg, setValidateMsg] = useState<ValidateMsg>({
    title: "",
    message: "",
  });

  const [notice, setNotice] = useState<Notice>(
    props.isEdit && props.selectedNotice !== undefined
      ? props.selectedNotice
      : {
          noticeId: "",
          subject: "",
          message: "",
          icon: ICON_TYPE.NONE,
          color: `BLUE` as NOTICE_COLOR,
          period: 1,
          isDisplay: false,
        }
  );

  /**
   * @todo Update SetNotice
   */

  const onChange = (e: any) => {
    let {
      target: { value, name },
    } = e;

    if (name === "period") {
      if (value === "0" || value === 0 || !isNumber(Number(value))) {
        setIsPermanently(true);
        return;
      }

      setNotice({
        ...notice,
        period: Number(value),
      });
      return;
    }
    if (name === "subject" && value.length > 255) {
      return;
    }

    setNotice({
      ...notice,
      [name]: value,
    });
  };

  const mutationCreateNotice = useStatusMutation(postNotice, {
    onSuccess: () => {
      notifySuccess(lang.label.setting.notice.notify.add.success);
      props.onModal();
      queryClient.invalidateQueries("getNoticeInNoticePage");
    },
    onError: () => {
      // setIsAddModal(false);
      notifyError(lang.label.setting.notice.notify.add.fail);
    },
  });

  const onValidate = (notice: Notice) => {
    if (checkEmpty(notice.subject)) {
      setValidateMsg((info) => {
        return {
          ...info,
          title: intlString(lang.validateMsg.notice.title),
        };
      });
      return true;
    }

    if (checkEmpty(notice.message)) {
      setValidateMsg((info) => {
        return {
          ...info,
          message: intlString(lang.validateMsg.notice.message.empty),
        };
      });
      return true;
    }

    if (extractTextFromHTML(notice.message).length > 500) {
      setValidateMsg((info) => {
        return {
          ...info,
          message: intlString(lang.validateMsg.notice.message.exceed),
        };
      });
      return true;
    }
    return false;
  };

  const onSave = () => {
    setValidateMsg((info) => {
      return { title: "", message: "" };
    });

    if (onValidate(notice)) {
      return;
    }
    const payload = {
      ...notice,
      message: toTrim(notice.message),
      subject: toTrim(notice.subject),
      period: isPermanently ? 0 : notice.period,
    };

    if (props.isEdit && props.selectedNotice) {
      if (props.selectedNotice.noticeId) {
        if (!mutationUpdateNotice.idle) return;
        mutationUpdateNotice.mutate({
          payload,
          noticeId: props.selectedNotice.noticeId,
          accountId: selectedAccount.accountId,
        });
      }
      return;
    }
    if (!mutationCreateNotice.idle) return;
    mutationCreateNotice.mutate({
      payload,
      accountId: selectedAccount.accountId,
    });
  };

  const mutationUpdateNotice = useStatusMutation(putNotice, {
    onSuccess: () => {
      notifySuccess(lang.label.setting.notice.notify.update.success);
      props.onModal();
      queryClient.invalidateQueries("getNoticeInNoticePage");
    },
    onError: () => {
      notifyError(lang.label.setting.notice.notify.update.fail);
    },
  });

  const mutationDeleteNotice = useStatusMutation(deleteNotice, {
    onSuccess: () => {
      notifySuccess(lang.label.setting.notice.notify.delete.success);
      queryClient.invalidateQueries("getNoticeInNoticePage");
      props.onModal();
    },
    onError: () => {
      notifyError(lang.label.setting.notice.notify.delete.fail);
    },
  });

  const onChangePermanent = (e: any) => {
    setIsPermanently(!isPermanently);
  };

  const onDelete = useCallback(() => {
    if (props.isEdit && props.selectedNotice) {
      if (!mutationDeleteNotice.idle) return;
      mutationDeleteNotice.mutate({
        accountId: selectedAccount.accountId,
        noticeId: props.selectedNotice.noticeId as string,
      });
    }
  }, [mutationDeleteNotice, props.selectedNotice, props.isEdit]);

  useEffect(() => {
    if (props.isEdit && props.selectedNotice) {
      if (props.selectedNotice.period === 0) {
        setIsPermanently(true);
      } else {
        setIsPermanently(false);
      }
    }
  }, [props.isEdit, props.selectedNotice]);

  return (
    <AddModalPresenter
      isEdit={props.isEdit}
      onModal={props.onModal}
      onChange={onChange}
      notice={notice}
      onDelete={onDelete}
      validateMsg={validateMsg}
      isPermanently={isPermanently}
      onChangePermanent={onChangePermanent}
      onSave={onSave}
    />
  );
}
